import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, updateProfile, sendPasswordResetEmail, deleteUser } from 'firebase/auth';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .cfg-page {
    min-height: 100vh;
    background: #0a1628;
    font-family: 'DM Sans', sans-serif;
    padding: 40px 20px 60px;
    color: #f0f4f8;
  }

  .cfg-container {
    max-width: 680px;
    margin: 0 auto;
  }

  .cfg-title {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  .cfg-card {
    background: #0f1e35;
    border: 1px solid rgba(0,173,181,0.15);
    border-radius: 14px;
    padding: 28px;
    margin-bottom: 20px;
  }

  .cfg-card__label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.35);
    margin-bottom: 20px;
  }

  .cfg-field {
    margin-bottom: 16px;
  }

  .cfg-field__label {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 6px;
    display: block;
  }

  .cfg-field__input {
    width: 100%;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    padding: 10px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #f0f4f8;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }

  .cfg-field__input:focus {
    border-color: rgba(0,173,181,0.5);
  }

  .cfg-field__input:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .cfg-btn {
    display: inline-block;
    background: #00adb5;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 22px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s;
  }

  .cfg-btn:hover {
    background: #008f96;
    transform: translateY(-1px);
  }

  .cfg-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .cfg-btn--ghost {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.7);
  }

  .cfg-btn--ghost:hover {
    background: rgba(255,255,255,0.06);
    border-color: rgba(255,255,255,0.4);
    color: #fff;
    transform: none;
  }

  .cfg-btn--danger {
    background: transparent;
    border: 1px solid rgba(255,87,34,0.4);
    color: #ff7043;
  }

  .cfg-btn--danger:hover {
    background: rgba(255,87,34,0.1);
    transform: none;
  }

  .cfg-feedback {
    font-size: 13px;
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: 6px;
  }

  .cfg-feedback--success {
    background: rgba(0,173,181,0.1);
    border: 1px solid rgba(0,173,181,0.25);
    color: #00adb5;
  }

  .cfg-feedback--error {
    background: rgba(255,87,34,0.08);
    border: 1px solid rgba(255,87,34,0.25);
    color: #ff7043;
  }

  .cfg-divider {
    height: 1px;
    background: rgba(255,255,255,0.06);
    margin: 20px 0;
  }

  .cfg-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .cfg-row__text {}

  .cfg-row__title {
    font-size: 14px;
    color: rgba(255,255,255,0.85);
    margin-bottom: 2px;
  }

  .cfg-row__desc {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
  }

  .cfg-empty {
    text-align: center;
    padding: 60px 20px;
    color: rgba(255,255,255,0.3);
    font-size: 15px;
  }

  .cfg-danger-zone .cfg-card {
    border-color: rgba(255,87,34,0.2);
  }
`;

export default function Configuracoes() {
  const [user, setUser]           = useState(null);
  const [name, setName]           = useState('');
  const [saving, setSaving]       = useState(false);
  const [nameFeedback, setNameFeedback] = useState(null); // { type, msg }
  const [resetFeedback, setResetFeedback] = useState(null);
  const [deleteFeedback, setDeleteFeedback] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const auth = getAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u ?? null);
      setName(u?.displayName || '');
    });
    return () => unsub();
  }, []);

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setNameFeedback(null);
    try {
      await updateProfile(auth.currentUser, { displayName: name.trim() });
      setNameFeedback({ type: 'success', msg: 'Nome atualizado com sucesso.' });
    } catch (err) {
      setNameFeedback({ type: 'error', msg: 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    setResetFeedback(null);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetFeedback({ type: 'success', msg: `E-mail de redefinição enviado para ${user.email}.` });
    } catch (err) {
      setResetFeedback({ type: 'error', msg: 'Não foi possível enviar o e-mail.' });
    }
  };

  const handleDeleteAccount = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleteFeedback(null);
    try {
      await deleteUser(auth.currentUser);
      // Firebase desloga automaticamente após exclusão
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setDeleteFeedback({ type: 'error', msg: 'Para excluir sua conta, faça login novamente e tente de novo.' });
      } else {
        setDeleteFeedback({ type: 'error', msg: 'Erro ao excluir conta.' });
      }
      setConfirmDelete(false);
    }
  };

  const isGoogleUser = user?.providerData?.[0]?.providerId === 'google.com';

  return (
    <>
      <style>{styles}</style>
      <div className="cfg-page">
        <div className="cfg-container">
          <h1 className="cfg-title">Configurações</h1>

          {!user ? (
            <div className="cfg-empty">Você não está autenticado.</div>
          ) : (
            <>
              {/* Nome */}
              <div className="cfg-card">
                <div className="cfg-card__label">Perfil</div>

                <div className="cfg-field">
                  <label className="cfg-field__label">Nome de exibição</label>
                  <input
                    className="cfg-field__input"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Seu nome"
                    maxLength={60}
                  />
                </div>

                <button
                  className="cfg-btn"
                  onClick={handleSaveName}
                  disabled={saving || !name.trim()}
                >
                  {saving ? 'Salvando…' : 'Salvar'}
                </button>

                {nameFeedback && (
                  <div className={`cfg-feedback cfg-feedback--${nameFeedback.type}`}>
                    {nameFeedback.msg}
                  </div>
                )}

                <div className="cfg-divider" />

                <div className="cfg-field">
                  <label className="cfg-field__label">E-mail</label>
                  <input
                    className="cfg-field__input"
                    type="email"
                    value={user.email}
                    disabled
                  />
                </div>
              </div>

              {/* Senha */}
              {!isGoogleUser && (
                <div className="cfg-card">
                  <div className="cfg-card__label">Segurança</div>
                  <div className="cfg-row">
                    <div className="cfg-row__text">
                      <div className="cfg-row__title">Redefinir senha</div>
                      <div className="cfg-row__desc">Enviaremos um link para o seu e-mail.</div>
                    </div>
                    <button className="cfg-btn cfg-btn--ghost" onClick={handlePasswordReset}>
                      Enviar e-mail
                    </button>
                  </div>
                  {resetFeedback && (
                    <div className={`cfg-feedback cfg-feedback--${resetFeedback.type}`} style={{ marginTop: 14 }}>
                      {resetFeedback.msg}
                    </div>
                  )}
                </div>
              )}

              {/* Zona de perigo */}
              <div className="cfg-danger-zone">
                <div className="cfg-card">
                  <div className="cfg-card__label">Zona de perigo</div>
                  <div className="cfg-row">
                    <div className="cfg-row__text">
                      <div className="cfg-row__title">Excluir conta</div>
                      <div className="cfg-row__desc">Esta ação é permanente e não pode ser desfeita.</div>
                    </div>
                    <button
                      className="cfg-btn cfg-btn--danger"
                      onClick={handleDeleteAccount}
                    >
                      {confirmDelete ? 'Confirmar exclusão' : 'Excluir conta'}
                    </button>
                  </div>
                  {confirmDelete && !deleteFeedback && (
                    <div className="cfg-feedback cfg-feedback--error" style={{ marginTop: 14 }}>
                      Clique em "Confirmar exclusão" para apagar sua conta permanentemente.{' '}
                      <span
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setConfirmDelete(false)}
                      >
                        Cancelar
                      </span>
                    </div>
                  )}
                  {deleteFeedback && (
                    <div className={`cfg-feedback cfg-feedback--${deleteFeedback.type}`} style={{ marginTop: 14 }}>
                      {deleteFeedback.msg}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
